import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '../lib/supabase';
import * as Sentry from "@sentry/react";

interface SubscriptionState {
  hasActiveSubscription: boolean;
  isLoading: boolean;
  error: string | null;
  planType?: string;
  expiresAt?: Date;
}

export const useSubscription = () => {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState<SubscriptionState>({
    hasActiveSubscription: false,
    isLoading: true,
    error: null
  });

  const checkSubscription = useCallback(async () => {
    if (!isAuthenticated) {
      setState({
        hasActiveSubscription: false,
        isLoading: false,
        error: null
      });
      return;
    }

    try {
      const { data: subscription, error } = await supabase
        .from('subscriptions')
        .select('status, current_period_end, plan_type')
        .eq('status', 'active')
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No subscription found
          setState({
            hasActiveSubscription: false,
            isLoading: false,
            error: null
          });
          return;
        }
        throw error;
      }

      const expiresAt = new Date(subscription.current_period_end);
      const isActive = !!subscription && expiresAt > new Date();

      setState({
        hasActiveSubscription: isActive,
        isLoading: false,
        error: null,
        planType: subscription?.plan_type,
        expiresAt: isActive ? expiresAt : undefined
      });

      // Log subscription check to Sentry for monitoring
      Sentry.addBreadcrumb({
        category: 'subscription',
        message: `Subscription check: ${isActive ? 'active' : 'inactive'} (${subscription?.plan_type})`,
        level: 'info',
        data: {
          planType: subscription?.plan_type,
          expiresAt: subscription?.current_period_end,
          status: subscription?.status
        }
      });

      // Warn if subscription is expiring soon
      const daysUntilExpiry = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (isActive && daysUntilExpiry <= 7) {
        Sentry.captureMessage(`Subscription expiring soon: ${daysUntilExpiry} days remaining`, {
          level: 'warning',
          extra: {
            planType: subscription?.plan_type,
            expiresAt: subscription?.current_period_end
          }
        });
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      Sentry.captureException(error);
      setState({
        hasActiveSubscription: false,
        isLoading: false,
        error: 'Failed to check subscription status'
      });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    checkSubscription();

    // Set up subscription to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkSubscription();
    });

    // Check subscription periodically
    const interval = setInterval(checkSubscription, 5 * 60 * 1000); // Every 5 minutes

    return () => {
      subscription.unsubscribe();
      clearInterval(interval);
    };
  }, [checkSubscription]);

  return state;
};